import { Template } from '../types/templates';
import { api } from './api';

// Re-exportando o tipo Template
export type { Template };

class TemplateService {
  async getTemplates(includeEmpty: boolean = false): Promise<Template[]> {
    try {
      const response = await api.get('/documents/templates', {
        params: { includeEmpty }
      });
      
      // Filtrar o modelo "empty" se não for solicitado
      let templates = response.data;
      if (!includeEmpty) {
        templates = templates.filter((template: Template) => 
          template.name.toLowerCase() !== 'empty'
        );
      }
      
      return templates;
    } catch (error) {
      console.error('Erro ao buscar templates:', error);
      throw error;
    }
  }

  async getTemplate(id: string): Promise<Template | undefined> {
    try {
      const response = await api.get(`/documents/templates/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar template:', error);
      throw error;
    }
  }

  async createTemplate(template: Omit<Template, 'id' | 'createdBy' | 'updatedAt'>): Promise<Template> {
    try {
      // Validação de nomes reservados
      const normalizedName = template.name.toLowerCase().replace(/\s+/g, '');
      if (normalizedName === 'default' || normalizedName === 'folder') {
        throw new Error('Os nomes "default" e "folder" são reservados e não podem ser utilizados');
      }

      // Validação de limites de caracteres
      if (template.name.length > 200) {
        throw new Error('O nome deve ter no máximo 200 caracteres');
      }

      if (template.description && template.description.length > 400) {
        throw new Error('A descrição deve ter no máximo 400 caracteres');
      }

      const response = await api.post('/documents/templates', template);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar template:', error);
      throw error;
    }
  }

  async updateTemplate(id: string, template: Partial<Template>): Promise<Template> {
    try {
      // Validação de nomes reservados (apenas se o nome estiver sendo atualizado)
      if (template.name) {
        const normalizedName = template.name.toLowerCase().replace(/\s+/g, '');
        if (normalizedName === 'default' || normalizedName === 'folder') {
          throw new Error('Os nomes "default" e "folder" são reservados e não podem ser utilizados');
        }

        // Validação de limites de caracteres
        if (template.name.length > 200) {
          throw new Error('O nome deve ter no máximo 200 caracteres');
        }
      }

      // Validação de limites de caracteres para descrição
      if (template.description && template.description.length > 400) {
        throw new Error('A descrição deve ter no máximo 400 caracteres');
      }

      const response = await api.put(`/documents/templates/${id}`, template);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar template:', error);
      throw error;
    }
  }

  async deleteTemplate(id: string): Promise<void> {
    try {
      await api.delete(`/documents/templates/${id}`);
    } catch (error) {
      console.error('Erro ao deletar template:', error);
      throw error;
    }
  }

  async updateTemplateStatus(id: string, isActive: boolean): Promise<Template> {
    try {
      const response = await api.put(`/documents/templates/${id}`, { isActive });
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar status do template:', error);
      throw error;
    }
  }

  async updateManyTemplates(templates: Template[]): Promise<Template[]> {
    try {
      const response = await api.put('/documents/templates/batch', templates);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar templates em lote:', error);
      throw error;
    }
  }

  async deleteManyTemplates(ids: string[]): Promise<void> {
    try {
      await api.delete('/documents/templates/batch', { data: ids });
    } catch (error) {
      console.error('Erro ao deletar templates em lote:', error);
      throw error;
    }
  }
}

export const templateService = new TemplateService(); 